import React, { useState } from 'react';
import BasePageLayout from '../components/BasePageLayout';
import { useLanguage } from '../context/LanguageContext';
import { showToast } from '../utils/toast';
import { ContactMessageService } from '../services/contactMessageService';
import '../styles/pages/ContactPage.css';
import '../styles/layout/wide-screen-layouts.css';
import '../styles/shared/dashboard.css';

const ContactPage: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit contact message
      const result = await ContactMessageService.createContactMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });

      if (result.success) {
        showToast(t('contactPage.thankYouMessage'), 'success');
        // Reset form
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        showToast(result.error || 'Failed to send message. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      showToast('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="contact-container">
        {/* Header Section - Matching Help Page */}
        <section className="contact-header-section">
          <div className="contact-header-content">
            <div className="contact-hero-centered">
              <div className="contact-icon-wrapper">
                <div className="contact-icon-large">
                  <i className="bi bi-envelope"></i>
                </div>
              </div>

              <div className="contact-info">
                <div className="contact-title-group">
                  <h1 className="contact-title-main">{t('contactPage.title')}</h1>
                  <span className="contact-badge">
                    <i className="bi bi-lightning-charge"></i>
                    {t('contactPage.quickResponse')}
                  </span>
                </div>

                <p className="contact-description">
                  {t('contactPage.description')}
                </p>

                <div className="contact-meta">
                  <div className="meta-item">
                    <i className="bi bi-clock"></i>
                    <span>{t('contactPage.hourResponse')}</span>
                  </div>
                  <div className="meta-item">
                    <i className="bi bi-headset"></i>
                    <span>{t('contactPage.expertSupport')}</span>
                  </div>
                  <div className="meta-item">
                    <i className="bi bi-shield-check"></i>
                    <span>{t('contactPage.securePrivate')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Grid - Similar to ProfilePage wide-grid */}
        <section className="contact-wide-grid">
          {/* Contact Form Panel */}
          <div className="contact-panel-form">
            <div className="contact-info-card">
              <h3 className="section-title">
                <i className="bi bi-send"></i>
                {t('contactPage.sendMessage')}
              </h3>

              <p className="form-description">
                {t('contactPage.formDescription')}
              </p>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">{t('contactPage.nameRequired')}</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contactPage.namePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">{t('contactPage.emailRequired')}</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contactPage.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">{t('contactPage.subjectRequired')}</label>
                  <select
                    className="form-control"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contactPage.selectSubject')}</option>
                    <option value="general">{t('contactPage.generalInquiry')}</option>
                    <option value="technical">{t('contactPage.technicalSupport')}</option>
                    <option value="business">{t('contactPage.businessPartnership')}</option>
                    <option value="feedback">{t('contactPage.feedbackSuggestions')}</option>
                    <option value="bug">{t('contactPage.bugReport')}</option>
                    <option value="account">{t('contactPage.accountIssues')}</option>
                    <option value="billing">{t('contactPage.billingPoints')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">{t('contactPage.messageRequired')}</label>
                  <textarea
                    className="form-control"
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder={t('contactPage.messagePlaceholder')}
                  />
                  <small className="form-help">
                    {t('contactPage.formHelp')}
                  </small>
                </div>

                <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting}>
                  <i className={isSubmitting ? "bi bi-hourglass-split" : "bi bi-send"}></i>
                  {isSubmitting ? 'Sending...' : t('contactPage.sendMessageButton')}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information Panel */}
          <div className="contact-panel-info">
            <div className="contact-info-card">
              <h3 className="section-title">
                <i className="bi bi-info-circle"></i>
                {t('contactPage.contactInformation')}
              </h3>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <i className="bi bi-envelope-fill"></i>
                  </div>
                  <div className="method-details">
                    <h4>{t('contactPage.emailSupportTitle')}</h4>
                    <p>{t('contactPage.emailAddress')}</p>
                    <small>{t('contactPage.responseTime')}</small>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <i className="bi bi-clock-fill"></i>
                  </div>
                  <div className="method-details">
                    <h4>{t('contactPage.supportHours')}</h4>
                    <p>{t('contactPage.mondayFriday')}</p>
                    <small>{t('contactPage.hoursUTC')}</small>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <i className="bi bi-chat-dots-fill"></i>
                  </div>
                  <div className="method-details">
                    <h4>{t('contactPage.community')}</h4>
                    <p>{t('contactPage.joinDiscord')}</p>
                    <small>{t('contactPage.realtimeSupport')}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Help Panel */}
          <div className="contact-panel-help">
            <div className="contact-info-card">
              <h3 className="section-title">
                <i className="bi bi-lightning-fill"></i>
                {t('contactPage.quickHelp')}
              </h3>

              <div className="quick-help-items">
                <div className="quick-help-item">
                  <h4>{t('contactPage.accountIssuesTitle')}</h4>
                  <p>{t('contactPage.accountIssuesDesc')}</p>
                  <a href="/help#account" className="btn btn-secondary">
                    {t('contactPage.viewGuide')}
                  </a>
                </div>

                <div className="quick-help-item">
                  <h4>{t('contactPage.pointsRewards')}</h4>
                  <p>{t('contactPage.pointsRewardsDesc')}</p>
                  <a href="/help#points" className="btn btn-secondary">
                    {t('contactPage.learnMore')}
                  </a>
                </div>

                <div className="quick-help-item">
                  <h4>{t('contactPage.taskCreation')}</h4>
                  <p>{t('contactPage.taskCreationDesc')}</p>
                  <a href="/help#tasks" className="btn btn-secondary">
                    {t('contactPage.getStarted')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Response Times Panel */}
          <div className="contact-panel-response">
            <div className="contact-info-card">
              <h3 className="section-title">
                <i className="bi bi-speedometer2"></i>
                {t('contactPage.responseTimes')}
              </h3>

              <div className="response-times">
                <div className="response-time-item">
                  <span className="priority high">{t('contactPage.highPriority')}</span>
                  <span className="time">{t('contactPage.highPriorityTime')}</span>
                </div>
                <div className="response-time-item">
                  <span className="priority medium">{t('contactPage.mediumPriority')}</span>
                  <span className="time">{t('contactPage.mediumPriorityTime')}</span>
                </div>
                <div className="response-time-item">
                  <span className="priority low">{t('contactPage.lowPriority')}</span>
                  <span className="time">{t('contactPage.lowPriorityTime')}</span>
                </div>
              </div>

              <div className="response-note">
                <i className="bi bi-info-circle"></i>
                {t('contactPage.priorityNote')}
              </div>
            </div>
          </div>

          {/* Social Links Panel */}
          <div className="contact-panel-social">
            <div className="contact-info-card">
              <h3 className="section-title">
                <i className="bi bi-share"></i>
                {t('contactPage.followUs')}
              </h3>

              <div className="social-links">
                <a href="https://www.linkedin.com/company/funderschool/about/?viewAsMember=true" className="social-link" target="_blank" rel="noopener noreferrer">
                  <div className="social-icon">
                    <i className="bi bi-linkedin"></i>
                  </div>
                  <span>{t('contactPage.linkedin')}</span>
                </a>
                <a href="https://twitter.com/jackieInfiDots" className="social-link" target="_blank" rel="noopener noreferrer">
                  <div className="social-icon">
                    <i className="bi bi-twitter"></i>
                  </div>
                  <span>{t('contactPage.twitter')}</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </BasePageLayout>
  );
};

export default ContactPage;
